import { gql } from '@apollo/client/core';

// Lightweight fragment used by list queries — no nested tier/addon collections.
const fragStickerList = gql`fragment stickerList on Sticker {
   id
   title
   slug
   url
   description
   entity
   entityId
   user {
     id
     slug
     firstname
     lastname
     avatar
   }
   organization {
    id
    slug
    name
    avatar
    email
    phone
   }
   published
   tags
   cost
   campaignType
   causes
   isCausesSearchAllowed
   nonProfitSuggestions
   maxQuantity
   maxDonation
   donationMultiplier
   impact
   eventDate
   eventDateTimeZone
   startDate
   startDateTimeZone
   endDate
   endDateTimeZone
   seen
   isSpotlight
   createdAt
   isFeatured
   location
   waiverURL
   status
   bannerUrl
   locationType
   virtualLink
   hideLocation
   confirmationEmail
   reminderEmail
   hasOptions
   quickCosts
   priceRange
   glow {
    glowStartDate
    glowEndDate
    glowColor
    glowStartDateTimeZone
    glowEndDateTimeZone
   }
   sweepstakesStickerId
   sweepstakesSticker {
    aboutTheCause
    numberOfPrizesAndWinners
    prizes
    nonProfitBeneficiary
    drawingDateTime
    drawingDateTimeTimezone
    drawnAt
    experienceDateTime
    experienceDateTimeTimezone
    approximateRetailValue
    sweepstakesConfirmation
    sweepstakesWinnerMessage
    sweepstakesLoserMessage
    checkoutPageLegal
    entries
    entryCost
  }
  isWinnerConfirmed
  viewCount
  shareCount
  requiresCollectionCode
}`;

// Full fragment used by single-sticker queries and mutations — includes all
// nested collections with every field needed by the CMS builder.
const fragSticker = gql`fragment sticker on Sticker {
   id
   title
   slug
   url
   description
   entity
   entityId
   user {
     id
     slug
     firstname
     lastname
     avatar
   }
   organization {
    id
    slug
    name
    avatar
    email
    phone
   }
   published
   tags
   cost
   campaignType
   causes
   isCausesSearchAllowed
   nonProfitSuggestions
   maxQuantity
   maxDonation
   donationMultiplier
   impact
   eventDate
   eventDateTimeZone
   startDate
   startDateTimeZone
   endDate
   endDateTimeZone
   seen
   isSpotlight
   createdAt
   isFeatured
   location
   waiverURL
   status
   bannerUrl
   locationType
   virtualLink
   hideLocation
   varients {
    id
    stickerId
    title
    cost
    description
    sortOrder
    isActive
    isFree
    whatsIncluded
    selectionOptions
    isAddOn
   }
   addOns {
    id
    type
    sortOrder
    donationCauses
    donationPricing
    donationFixed
    donationSuggested
    sweepstakesStickerId
    glowIsExisting
    glowId
    glowName
    glowDescription
    glowIsFree
    glowPrice
   }
   gallery {
    id
    type
    url
    thumbnail
    sortOrder
   }
   faqs {
    id
    question
    answer
    sortOrder
   }
   confirmationEmail
   reminderEmail
   hasOptions
   quickCosts
   priceRange
   glow {
    glowStartDate
    glowEndDate
    glowColor
    glowStartDateTimeZone
    glowEndDateTimeZone
   }
   sweepstakesStickerId
   sweepstakesSticker {
    aboutTheCause
    numberOfPrizesAndWinners
    prizes
    nonProfitBeneficiary
    drawingDateTime
    drawingDateTimeTimezone
    drawnAt
    experienceDateTime
    experienceDateTimeTimezone
    approximateRetailValue
    sweepstakesConfirmation
    sweepstakesWinnerMessage
    sweepstakesLoserMessage
    checkoutPageLegal
    entries
    entryCost
  }
  isWinnerConfirmed
  viewCount
  shareCount
  requiresCollectionCode
}`;

const fragSweepstakeWinners = gql`
  fragment sweepstakeWinners on SweepstakeWinners {
    id
    stickerId
    entityId
    participantName
    participantAvatar
    participantEmail
    eligible
    notifiedAt
    dateCreated
    dateUpdated
  }
`;

export const findStickers = gql`
${fragStickerList}
query(
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $filter: [String]!,
  $orderBy: String,
  $orderAsc: Boolean,
  $organizationId: String
) {
  findStickers(
    take: $take,
    skip: $skip,
    searchString: $searchString,
    filter: $filter,
    orderBy: $orderBy,
    orderAsc: $orderAsc,
    organizationId: $organizationId
  ) {
     ...stickerList
  }
}`;

export const findOrgStickers = gql`
${fragStickerList}
query($orgId: String!) {
  findOrgStickers(orgId: $orgId) {
     ...stickerList
  }
}`;

// Org stickers page: list fields plus the sales numbers the portal table shows.
export const findOrgStickersWithStats = gql`
${fragStickerList}
query($orgId: String!) {
  findOrgStickers(orgId: $orgId) {
     ...stickerList
     raised
     donors
     grossRaised
     netRaised
     unitsSold
     affiliatesCount
  }
}`;

export const findApprovedOrgStickers = gql`
${fragStickerList}
query($orgId: String!, $searchString: String) {
  findApprovedOrgStickers(orgId: $orgId, searchString: $searchString) {
     ...stickerList
  }
}`;

export const findApprovedOrgStickersBySlug = gql`
${fragStickerList}
query($slug: String!, $searchString: String) {
  findApprovedOrgStickersBySlug(slug: $slug, searchString: $searchString) {
     ...stickerList
  }
}`;

export const findPublishedStickers = gql`
${fragStickerList}
query($filter: [String]!, $take: Int!, $skip: Int!, $searchString: String) {
  findPublishedStickers(filter: $filter, take: $take, skip: $skip, searchString: $searchString) {
     ...stickerList
  }
}`;

export const findPublishedStickersWithFiltering = gql`
${fragStickerList}
query($filter: [String]!, $take: Int!, $skip: Int!, $searchString: String) {
  findPublishedStickers(filter: $filter, take: $take, skip: $skip, searchString: $searchString) {
     ...stickerList
  }
}`;

export const findPublishedStickersShortDetails = gql`
query(
  $filter: [String!]!,
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $socialShopUserId: UUID
) {
  findPublishedStickersShortDetails(
    filter: $filter,
    take: $take,
    skip: $skip,
    searchString: $searchString,
    socialShopUserId: $socialShopUserId
  ) {
    id
    title
    url
    cost
    published
    entity
    campaignType
    priceRange
    quickCosts
    organization {
      id
      slug
      name
      avatar
    }
    user {
      id
      slug
      firstname
      lastname
      avatar
    }
  }
}`;

export const findPurchasedStickers = gql`
${fragStickerList}
query($entity: String! $entityId: String!) {
  findPurchasedStickers(entity: $entity, entityId: $entityId) {
     ...stickerList
   }
}`;

/** The caller's ticketed events, one row per event with its ticket count (My Tickets → Upcoming). */
export const findMyTicketPurchases = gql`
query FindMyTicketPurchases(
  $searchString: String, $timeFilter: String, $skip: Int!, $take: Int!, $orgId: String
) {
  findMyTicketPurchases(
    searchString: $searchString, timeFilter: $timeFilter, skip: $skip, take: $take, orgId: $orgId
  ) {
    ticketCount
    totalPaid
    purchaseDate
    sticker {
      id
      title
      url
      campaignType
      eventDate
      eventDateTimeZone
    }
  }
}`;

export const findPurchasedStickersHistory = gql`
query FindPurchasedStickersHistory(
  $entity: String!
  $entityId: String!
  $skip: Int!
  $take: Int!
  $searchString: String
  $stickerType: String
  $timeFilter: String
) {
  findPurchasedStickersHistory(
    entityId: $entityId
    entity: $entity
    skip: $skip
    take: $take
    searchString: $searchString
    stickerType: $stickerType
    timeFilter: $timeFilter
  ) {
    id
    purchasePrice
    purchaseDate
    sticker {
      id
      title
      url
      campaignType
      entity
      entityId
      cost
      eventDate
      eventDateTimeZone
      organization {
        id
        name
        avatar
      }
      user {
        id
        firstname
        lastname
        avatar
      }
    }
  }
}`;

export const findSticker = gql`
${fragSticker}
query($id: String!) {
   findSticker(id: $id) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const findStickerBySlug = gql`
${fragSticker}
query($slug: String!) {
   findStickerBySlug(slug: $slug) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const findOrgSticker = gql`
${fragSticker}
query($orgId: String! $id: String!) {
   findOrgSticker(orgId: $orgId, id: $id) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const mutateSticker = gql`
${fragSticker}
mutation($input: StickerInput!) {
   mutateSticker(input: $input) {
     ...sticker
   }
}`;

export const confirmSweepstakesWinners = gql`
mutation($sweepstakesStickerId: UUID!) {
  confirmSweepstakesWinners(sweepstakesStickerId: $sweepstakesStickerId)
}`;

// Public. Unlike the sticker payload's sweepstakesSticker, this carries the lifecycle stamps.
export const getSweepstakesLifecycle = gql`
mutation($sweepstakesStickerId: UUID!) {
  getSweepstakesStickerData(sweepstakesStickerId: $sweepstakesStickerId) {
    id
    numberOfPrizesAndWinners
    drawingDateTime
    drawingDateTimeTimezone
    drawnAt
    losersNotifiedAt
  }
}`;

export const drawSweepstakesWinners = gql`
  ${fragSweepstakeWinners}
  mutation($sweepstakesStickerId: UUID!, $participantIds: [UUID!]) {
    drawSweepstakesWinners(
      sweepstakesStickerId: $sweepstakesStickerId,
      participantIds: $participantIds
    ) {
      ...sweepstakeWinners
    }
  }
`;

export const getSweepstakesWinners = gql`
  ${fragSweepstakeWinners}
    mutation($sweepstakesStickerId: UUID!) {
    getSweepstakesWinners(sweepstakesStickerId: $sweepstakesStickerId) {
      ...sweepstakeWinners
    }
  }`;

export const mutateRemoveSticker = gql`
mutation($id: String!) {
  mutateRemoveSticker(id: $id)
}`;

export const mutateOrgSticker = gql`
${fragSticker}
mutation($orgId: String!, $input: StickerInput!) {
   mutateOrgSticker(orgId: $orgId, input: $input) {
     ...sticker
   }
}`;

export const mutateRemoveOrgSticker = gql`
mutation($orgId: String!, $id: String!) {
  mutateRemoveOrgSticker(orgId: $orgId id: $id)
}`;

export const findAllSpots = gql`
query {
  findAllSpots {
    id
    title
    description
    coverMediaUrl
    coverMediaType
    thumbnailUrl
    entity
    sortOrder
    organization{
      name
      avatar
    }
    user{
      firstname
      lastname
      avatar
    }
    moodboards{
      id
      title
      metadata
      url
    }
   }
}`;

export const getCMSHomeScreenData = gql`
query {
  getCMSHomeScreenData {
    id
    orgCollectionTitle
    stickerCollectionTitle
    spotCollectionTitle
    metaCarousel {
      id
      title
      image
      order
    }
    metaFeaturedOrgs {
      id
      title
      image
      order
    }
    metaFeaturedStickers {
      id
      title
      image
      order
    }
    metaFeaturedSpots {
      id
      title
      image
      order
    }
  }
}`;

export const mutateCMSHomeScreenData = gql`
mutation($input: HomeScreenDataInput!) {
  mutateCMSHomeScreenData(input: $input) {
    id
    orgCollectionTitle
    stickerCollectionTitle
    spotCollectionTitle
    metaCarousel {
      id
      title
      image
      order
    }
    metaFeaturedOrgs {
      id
      title
      image
      order
    }
    metaFeaturedStickers {
      id
      title
      image
      order
    }
    metaFeaturedSpots {
      id
      title
      image
      order
    }
  }
}`;

export const isStickerPurchased = gql`
query($entity: String! $entityId: String! $stickerId: String!) {
  isStickerPurchased(entity: $entity, entityId: $entityId, stickerId: $stickerId)
}`;

export const mutateStickerStatus = gql`
mutation($input: [StickerStatusInput!]!) {
  mutateStickerStatus(input: $input) {
    id
    status
  }
}`;

export const mutateStickerVarient = gql`
mutation($input: StickerVarientInput!) {
  mutateStickerVarient(input: $input) {
    id
    isActive
  }
}`;

export const getStickerInsights = gql`
query($stickerId: String!) {
  getStickerInsights(stickerId: $stickerId) {
    totalRevenue
    unitsSold
    purchasedUsersCount
    varients {
      id
      stickerId
      title
      cost
      description
      sortOrder
      isActive
      totalRevenue
      unitsSold
    }
    socialShopSummary {
      isFromSocialShop
      socialShopEntity
      isSocialShopOwnerShared
      socialShopEntityId
      socialShopName
      socialShopAvatar
      unitsSold
      totalRevenue
    }
    directSalesSummary {
      unitsSold
      totalRevenue
    }
    causeSummary {
      key
      value
    }
    benevityCausesName
  }
}`;

export const postGiftSticker = gql`
mutation($input: GiftStickerInput!) {
  giftSticker(input: $input)
}`;

export const collectFreeSticker = gql`
mutation($stickerId: String!, $socialShopId: UUID, $glowId: UUID) {
  collectFreeSticker(stickerId: $stickerId, socialShopId: $socialShopId, glowId: $glowId)
}`;

export const collectFreeStickerForOrg = gql`
mutation($orgId: String!, $stickerId: String!, $socialShopId: UUID, $glowId: UUID) {
  collectFreeStickerForOrg(
    orgId: $orgId, stickerId: $stickerId, socialShopId: $socialShopId, glowId: $glowId
  )
}`;

export const collectAnonymousFreeSticker = gql`
mutation(
  $stickerId: String!,
  $email: String!,
  $phone: String!,
  $firstName: String!,
  $lastName: String!,
  $embedOrganizationId: UUID,
  $embedHost: String,
  $socialShopId: UUID,
  $glowId: UUID
) {
  collectAnonymousFreeSticker(
    stickerId: $stickerId,
    email: $email,
    phone: $phone,
    firstName: $firstName,
    lastName: $lastName,
    embedOrganizationId: $embedOrganizationId,
    embedHost: $embedHost,
    socialShopId: $socialShopId,
    glowId: $glowId
  )
}`;

export const toggleStickerFavorite = gql`
mutation($stickerId: UUID!, $orgId: String) {
  toggleStickerFavorite(stickerId: $stickerId, orgId: $orgId)
}`;

export const findMyFavorites = gql`
${fragStickerList}
query($orgId: String) {
  findMyFavorites(orgId: $orgId) {
    ...stickerList
  }
}`;

export const mutateSocialShopSticker = gql`
mutation($stickerId: UUID!, $orgId: String) {
  mutateSocialShopSticker(
    input: { sortOrder: 0, stickerId: $stickerId }
    orgId: $orgId
  ) {
    id
  }
}`;

export const mutateRemoveSocialShopSticker = gql`
mutation($stickerId: String!, $orgId: String) {
  mutateRemoveSocialShopSticker(stickerId: $stickerId, orgId: $orgId)
}`;

export const findPublishedStickersShortDetailsWithSocialShop = gql`
query(
  $filter: [String!]!,
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $socialShopUserId: UUID,
  $socialShopOrgId: UUID,
  $socialShopGroupId: UUID
) {
  findPublishedStickersShortDetails(
    filter: $filter,
    take: $take,
    skip: $skip,
    searchString: $searchString,
    socialShopUserId: $socialShopUserId,
    socialShopOrgId: $socialShopOrgId,
    socialShopGroupId: $socialShopGroupId
  ) {
    id
    title
    url
    cost
    published
    entity
    campaignType
    priceRange
    quickCosts
    socialShop {
      id
      sortOrder
      canViewStats
      views
      revenue
    }
    organization {
      id
      slug
      name
      avatar
    }
    user {
      id
      slug
      firstname
      lastname
      avatar
    }
  }
}`;

export const trackSocialShopStickerView = gql`
mutation($socialShopId: UUID!) {
  trackSocialShopStickerView(socialShopId: $socialShopId)
}`;

export const getStickerAnalytics = gql`
query($stickerId: UUID!, $orgId: UUID) {
  getStickerAnalytics(stickerId: $stickerId, orgId: $orgId) {
    campaignType
    totalRaised
    grossRevenue
    totalViews
    totalShares
    totalAddToShop
    advocateRaised
    advocateTransactions
    totalTransactions
    distinctBuyers
    audience {
      isSuppressed
      totalBuyers
      knownAgeBuyers
      buckets {
        bucket
        count
      }
    }
    ticket {
      totalRsvps
      checkedInCount
      tiers {
        variantId
        title
        sold
        checkedIn
        capacity
        revenue
      }
    }
    campaign {
      totalRaised
      totalTransactions
      causes {
        causeId
        name
        amount
        transactions
      }
    }
    sweepstakes {
      isWinnerConfirmed
      drawingDateTime
      drawnAt
      losersNotifiedAt
      numberOfWinners
      totalEntries
      prizes
      winners {
        id
        entityId
        name
        avatar
        eligible
        selectedAt
        notifiedAt
      }
    }
  }
}`;

export const trackStickerView = gql`
mutation($stickerId: UUID!) {
  trackStickerView(stickerId: $stickerId)
}`;

export const trackStickerShare = gql`
mutation($stickerId: UUID!) {
  trackStickerShare(stickerId: $stickerId)
}`;
